/* ──────────────────────────────────────────────────────────────────────────
 * Geospatial service — turns domain records into GeoJSON for MapLibre.
 *
 * Kept separate from both the data and the map component (§15, clean
 * separation): the data files know nothing about GeoJSON, and the map knows
 * nothing about sectors or ghats. Either side can be replaced without
 * touching the other — which is the point, because the data side is the part
 * that gets swapped for real department feeds.
 *
 * Provenance travels into feature properties, so a clicked feature can show
 * its trust badge without a second lookup.
 * ────────────────────────────────────────────────────────────────────────── */

import {
  SECTORS, GHATS, ENTRY_GATES, EXIT_GATES, FACILITIES, ROUTES, CONGREGATION,
  sectorStatus, STATUS_COLOR,
} from "../data/kumbhGeo";
import { INCIDENTS, SEVERITY, INCIDENT_TYPES } from "../data/operational";
import { LAYER_BY_ID } from "../config/layers";

const fc = (features) => ({ type: "FeatureCollection", features });

/* MapLibre feature properties must be flat primitives — nested objects get
 * stringified unpredictably across renderers. Flatten deliberately. */
function baseProps(record, layerId, lang) {
  const layer = LAYER_BY_ID[layerId];
  return {
    id: record.id,
    layer: layerId,
    name: record.name?.[lang] ?? record.name?.en ?? record.id,
    nameEn: record.name?.en ?? record.id,
    color: layer?.color ?? "#475569",
    trust: record.provenance?.trust ?? "SIMULATED",
    source: record.provenance?.source ?? "",
    owner: record.provenance?.owner ?? "",
    note: record.provenance?.note ?? "",
    classification: record.provenance?.classification ?? "PUBLIC",
  };
}

const point = (record, layerId, lang, extra = {}) => ({
  type: "Feature",
  geometry: { type: "Point", coordinates: record.coord },
  properties: { ...baseProps(record, layerId, lang), ...extra },
});

/* ── Point layers ──────────────────────────────────────────────────────
 * One collection for every point layer, keyed by layer id, so the map can
 * loop the catalogue rather than hardcoding each. */
export function buildPointCollections(lang = "en") {
  const out = {};

  out.ghats = fc(GHATS.map((g) =>
    point(g, "ghats", lang, { principal: g.principal ? 1 : 0, sector: g.sector })));

  out.entryGates = fc(ENTRY_GATES.map((g) => point(g, "entryGates", lang, { sector: g.sector })));
  out.exitGates  = fc(EXIT_GATES.map((g) => point(g, "exitGates", lang, { sector: g.sector })));

  // Facilities carry their own layer id, so group them into per-layer sets.
  FACILITIES.forEach((f) => {
    if (!out[f.layer]) out[f.layer] = fc([]);
    out[f.layer].features.push(point(f, f.layer, lang, { sector: f.sector }));
  });

  return out;
}

/* ── Sector polygons ───────────────────────────────────────────────────
 * Status colour is computed here, not in the style, so the G/A/R rule lives
 * in one place (kumbhGeo.sectorStatus) rather than being restated in paint
 * expressions. */
export function buildSectorCollection(lang = "en") {
  return fc(SECTORS.map((s) => {
    const status = sectorStatus(s);
    return {
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [[...s.polygon, s.polygon[0]]] },
      properties: {
        ...baseProps(s, "sectors", lang),
        status,
        statusColor: STATUS_COLOR[status],
        capacity: s.capacity,
        current: s.current,
        ratioPct: Math.round((s.current / s.capacity) * 100),
      },
    };
  }));
}

export function buildCongregationCollection(lang = "en") {
  return fc(CONGREGATION.map((c) => ({
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [[...c.polygon, c.polygon[0]]] },
    properties: baseProps(c, "congregation", lang),
  })));
}

/* ── Route lines ───────────────────────────────────────────────────────── */
export function buildRouteCollections(lang = "en") {
  const out = {};
  ROUTES.forEach((r) => {
    if (!out[r.layer]) out[r.layer] = fc([]);
    out[r.layer].features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: r.coords },
      properties: baseProps(r, r.layer, lang),
    });
  });
  return out;
}

/* ── Crowd heatmap ─────────────────────────────────────────────────────── */
export function buildCrowdCollection(points) {
  return fc(points.map((p, i) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: p.coord },
    properties: { id: `CROWD-${i}`, weight: p.weight, sector: p.sector },
  })));
}

/* ── Incidents ─────────────────────────────────────────────────────────── */
export function buildIncidentCollection(incidents = INCIDENTS, lang = "en") {
  return fc(incidents.map((i) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: i.coord },
    properties: {
      id: i.id,
      layer: "incidents",
      type: i.type,
      typeLabel: INCIDENT_TYPES[i.type]?.label?.[lang] ?? i.type,
      severity: i.severity,
      severityRank: SEVERITY[i.severity]?.rank ?? 0,
      color: SEVERITY[i.severity]?.color ?? "#64748B",
      status: i.status,
      name: i.location?.[lang] ?? i.location?.en ?? i.id,
      trust: i.provenance?.trust ?? "SIMULATED",
    },
  })));
}

/* ── Flow arrows ───────────────────────────────────────────────────────── */
export function buildFlowCollection(vectors, lang = "en") {
  return fc(vectors.map((f) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: f.coord },
    properties: { id: f.id, bearing: f.bearing, rate: f.rate, sector: f.from },
  })));
}
